/**
 * Builds a query string compatible with the backend's expected format.
 * Mirrors the legacy `prepareUrlForQuery` from `src/api/actions.js`.
 *
 * @param {Object} params
 * @param {Object} [params.filters]   - e.g. { id: 1, name: 'foo' } → filters[id]=1&filters[name]=foo
 * @param {Object} [params.range]     - e.g. { page: 1, limit: 25 }
 * @param {Object} [params.sort]      - e.g. { field: 'name', direction: 'ASC' }
 * @param {Object} [params.extra]     - any additional flat key-value pairs
 * @returns {string} query string including leading '?', or empty string
 */
export function buildQueryString(params = {}) {
  const { filters, range, sort, ...rest } = params;
  const parts = [];

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        // Serialise as `filters[key][]=a&filters[key][]=b` — the qs default
        // Express uses on the other side accepts both `brackets` and
        // `indices`, but `[]` is the conventional form.
        value.forEach((entry) => {
          if (entry === undefined || entry === null) return;
          parts.push(
            `filters[${encodeURIComponent(key)}][]=${encodeURIComponent(entry)}`,
          );
        });
      } else {
        parts.push(`filters[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`);
      }
    });
  }

  if (range) {
    if (range.page !== undefined) parts.push(`range[page]=${range.page}`);
    if (range.limit !== undefined) parts.push(`range[limit]=${range.limit}`);
  }

  if (sort) {
    const { field, direction } = sort;
    if (field) {
      const sortKey = field.split('.').reduce((key, part) => `${key}[${part}]`, 'sort');
      parts.push(`${sortKey}=${encodeURIComponent(direction || 'ASC')}`);
    }
  }

  // Pass-through any remaining params
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}
